import { useCallback } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { useDialogStore } from '@core/stores/dialog.store';
import { DialogOverlay, DialogPanel } from './Dialog';

/**
 * Renders the dialog STACK. Mounted once in `providers.tsx`.
 *
 * A stack rather than a single slot because the Angular app nests dialogs:
 * `dashboard.component.ts:1202` opens a confirm from inside another confirm's result
 * handler, and `attributes-dialog` opens a dirty-check confirm over itself.
 *
 * `disableClose` maps to preventDefault on Radix's escape / outside-interaction
 * events — the faithful equivalent of `MatDialogConfig.disableClose`. Non-top entries
 * also preventDefault so a click landing outside a nested dialog cannot dismiss the
 * one underneath it.
 *
 * Exit animations are handled by Radix's Presence (it keeps Content mounted until the
 * CSS animation ends); the store drops the entry after a matching grace period.
 */
export function DialogHost() {
  const stack = useDialogStore((state) => state.stack);
  const requestClose = useDialogStore((state) => state.requestClose);

  const handleOpenChange = useCallback(
    (id: string, open: boolean) => {
      if (!open) requestClose(id, undefined);
    },
    [requestClose],
  );

  return (
    <>
      {stack.map((entry, index) => {
        const { id, Component, data, options, closing } = entry;
        const isTop = index === stack.length - 1;
        const block = options.disableClose === true;

        return (
          <RadixDialog.Root
            key={id}
            open={!closing}
            onOpenChange={(open) => handleOpenChange(id, open)}
          >
            <RadixDialog.Portal>
              {/* Only the bottom-most overlay is fully opaque — stacked backdrops
                  would otherwise multiply into a near-black screen. */}
              <DialogOverlay className={index > 0 ? 'bg-black/16' : undefined} />
              <DialogPanel
                width={options.width}
                maxWidthVw={options.maxWidthVw}
                maxHeightVh={options.maxHeightVh}
                panelClass={options.panelClass}
                onEscapeKeyDown={(event) => {
                  if (block || !isTop) event.preventDefault();
                }}
                onPointerDownOutside={(event) => {
                  if (block || !isTop) event.preventDefault();
                }}
                onInteractOutside={(event) => {
                  if (block || !isTop) event.preventDefault();
                }}
                onOpenAutoFocus={(event) => {
                  if (options.autoFocus === false) event.preventDefault();
                }}
              >
                <Component data={data} close={(result?: unknown) => requestClose(id, result)} />
              </DialogPanel>
            </RadixDialog.Portal>
          </RadixDialog.Root>
        );
      })}
    </>
  );
}
